import * as yup from 'yup';
import DOMPurify from 'dompurify';

/**
 * Forbidden elements that should not appear in iframe content
 */
const FORBIDDEN_ELEMENTS = ['<nav', '<header', '<footer'];

/**
 * Validate that forbidden elements are not present
 */
function validateForbiddenElements(html: string): { valid: boolean; found: string[] } {
  const found: string[] = [];

  FORBIDDEN_ELEMENTS.forEach((element) => {
    if (html.toLowerCase().includes(element)) {
      found.push(element);
    }
  });

  return {
    valid: found.length === 0,
    found,
  };
}

/**
 * Validate code snippet structure
 */
function validateCodeSnippets(html: string): { valid: boolean; warnings: string[] } {
  const warnings: string[] = [];

  // Check for plain pre/code without skillzen structure
  const hasPlainCode = /<pre[^>]*>\s*<code(?!.*skillzen-code-line)/.test(html);
  if (hasPlainCode) {
    warnings.push('Code blocks should use skillzen-code-snippet structure for consistent styling');
  }

  return {
    valid: true, // Just warnings, not blockers
    warnings,
  };
}

/**
 * Validate knowledge check structure
 */
function validateKnowledgeChecks(html: string): { valid: boolean; warnings: string[] } {
  const warnings: string[] = [];

  // Check for knowledge checks without unique IDs
  const checkpoints = html.match(/<div class="learning-checkpoint">/g);
  const answerIds = html.match(/id="(answer-\d+)"/g);

  if (checkpoints && checkpoints.length > 0) {
    if (!answerIds || answerIds.length !== checkpoints.length) {
      warnings.push('Some knowledge checks may be missing unique answer IDs');
    }

    // Check if all answer IDs are unique
    if (answerIds) {
      const ids = answerIds.map(id => id.match(/id="([^"]+)"/)?.[1]);
      const uniqueIds = new Set(ids);
      if (uniqueIds.size !== ids.length) {
        warnings.push('Duplicate answer IDs found - each knowledge check must have a unique ID');
      }
    }
  }

  return {
    valid: true,
    warnings,
  };
}

/**
 * Validation schema for estimated time
 */
const estimatedTimeSchema = yup.object().shape({
  hours: yup
    .number()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return 0;
      }
      return value;
    })
    .min(0, 'Hours must be at least 0')
    .max(100, 'Hours cannot exceed 100')
    .integer('Hours must be a whole number')
    .optional(),
  minutes: yup
    .number()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return 0;
      }
      return value;
    })
    .min(0, 'Minutes must be at least 0')
    .max(59, 'Minutes cannot exceed 59')
    .integer('Minutes must be a whole number')
    .optional(),
});

/**
 * Validation schema for media assets
 */
const mediaAssetsSchema = yup.object().shape({
  s3_audio_url: yup
    .string()
    .url('Audio URL must be a valid URL')
    .optional()
    .nullable(),
});

/**
 * Main validation schema for content item editing
 */
export const contentItemValidationSchema = yup.object().shape({
  title: yup
    .string()
    .required('Title is required')
    .trim()
    .min(1, 'Title cannot be empty')
    .max(200, 'Title cannot exceed 200 characters'),

  html_content: yup
    .string()
    .required('Content is required')
    .min(10, 'Content must be at least 10 characters')
    .test('no-forbidden-elements', 'Content contains navigation elements (nav, header, footer) that will cause display issues in the viewer. Please remove these elements.', (value) => {
      if (!value) return true;
      const { valid } = validateForbiddenElements(value);
      return valid;
    }),

  narration: yup
    .string()
    .trim()
    .max(5000, 'Narration cannot exceed 5000 characters')
    .optional()
    .nullable(),

  estimated_time: estimatedTimeSchema.optional().nullable(),

  media_assets: mediaAssetsSchema.optional().nullable(),
});

/**
 * Build the content-item schema for a given content type.
 * Plain `html` items have no narration UI, so the 5000-char narration limit must
 * NOT block saving them (a long seeded narration would otherwise be unfixable).
 * Audio/narrated types (html_with_audio, etc.) keep the narration length check.
 */
export const getContentItemValidationSchema = (options?: { validateNarration?: boolean }) => {
  const validateNarration = options?.validateNarration ?? true;
  return contentItemValidationSchema.shape({
    narration: validateNarration
      ? yup.string().trim().max(5000, 'Narration cannot exceed 5000 characters').optional().nullable()
      : yup.string().trim().optional().nullable(),
  });
};

/**
 * Type-safe validation function for content items
 */
export const validateContentItem = async (data: any) => {
  try {
    await contentItemValidationSchema.validate(data, { abortEarly: false });
    return { valid: true, errors: {}, warnings: [] };
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const errors: Record<string, string> = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      return { valid: false, errors, warnings: [] };
    }
    return { valid: false, errors: { _form: 'Validation failed' }, warnings: [] };
  }
};

/**
 * Extended validation with warnings for content quality
 */
export const validateContentItemExtended = async (data: any) => {
  const basicValidation = await validateContentItem(data);

  if (!basicValidation.valid) {
    return basicValidation;
  }

  // Run additional quality checks (warnings only)
  const warnings: string[] = [];

  if (data.html_content) {
    const codeValidation = validateCodeSnippets(data.html_content);
    warnings.push(...codeValidation.warnings);

    const checkValidation = validateKnowledgeChecks(data.html_content);
    warnings.push(...checkValidation.warnings);
  }

  return {
    valid: true,
    errors: {},
    warnings,
  };
};

/**
 * TypeScript interface for content item data
 */
export interface ContentItemFormData {
  title: string;
  html_content: string;
  narration?: string | null;
  estimated_time?: {
    hours?: number;
    minutes?: number;
  } | null;
  media_assets?: {
    s3_audio_url?: string | null;
  } | null;
}
